import { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";
import axios from "axios";
import { Reveal } from "./Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_STATS = [
  { label: "Food boxes delivered to homes", value: 150, suffix: "" },
  { label: "Food boxes & blankets given on the streets", value: 210, suffix: "" },
  { label: "Toys given to children", value: 250, suffix: "" },
  { label: "Volunteers", value: 10, suffix: "" },
];

const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref} className="font-display font-light text-6xl sm:text-7xl text-[#1A2F24]">
      {val.toLocaleString()}{suffix}
    </span>
  );
};

const Impact = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(FALLBACK_STATS);
  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => {
      if (r.data.items?.length) setStats(r.data.items);
    }).catch(() => {});
  }, []);

  return (
  <section className="py-28 lg:py-36" data-testid="impact-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-6 lg:gap-10 items-center mb-16">
        <Reveal className="col-span-12 md:col-span-7">
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C7368] mb-6">{t("imp_kicker")}</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight text-[#1A2F24]">{t("imp_title_a")}<em className="text-[#5C7368]">{t("imp_title_em")}</em></h2>
        </Reveal>
        <Reveal delay={0.15} className="col-span-12 md:col-span-5">
          <div className="rounded-2xl overflow-hidden border border-[#E2E8E4] shadow-[0_30px_60px_-30px_rgba(30,53,42,0.35)]">
            <img src="/images/family-support.jpg" alt={t("hero_alt")} className="w-full h-64 md:h-80 object-cover" data-testid="impact-image" />
          </div>
        </Reveal>
      </div>
      <div className="grid grid-cols-12 gap-4 sm:gap-8">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.12} className="col-span-6 lg:col-span-3">
            <div className="rounded-2xl border border-[#E2E8E4] bg-[#F2F4F0] p-10" data-testid={`impact-stat-${i}`}>
              <Counter to={s.value} suffix={s.suffix} />
              <p className="mt-4 text-xs tracking-[0.2em] uppercase text-[#5C7368]">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

export default Impact;
